import log from '../../lib/log'
import createLogger from 'debug'
import { useDispatch } from 'react-redux'

// to enable localStorage.debug = 'info:*'
const info = createLogger('info:api')

const actionRequest = (type, opts) => {
  return {
    type: type,
    payload: {
      opts
    }
  }
}

const actionSuccess = (type, opts, result) => {
  return {
    type: type,
    payload: {
      opts,
      result
    }
  }
}

const actionFailure = (type, opts, error) => {
  return {
    type: type,
    payload: {
      opts,
      error
    }
  }
}

const camelize = s => s && s[0].toUpperCase() + s.slice(1)

const guessAPIName = (operationType, entityType) =>
  `${operationType}${camelize(entityType)}`

export type CRUD_OPERATIONS = 'insert' | 'load' | 'update' | 'delete' | 'upsert'
export type EntityTypes = "draft" | "post" | "drafts" | "responses" | "members" | "discover"
export type OperationResult = "request" | "success" | "failure"
export const crudActionType = (operationType: CRUD_OPERATIONS, entityType: EntityTypes, result: OperationResult) => {
  const base = `${operationType}-${entityType}`
  return `${base}-${result}`
}

export const updateSuccessAction = <T = any>(entityType: EntityTypes, result: T, opts?) => {
  return {
    type: crudActionType("update", entityType, "success"),
    payload: {
      opts,
      result
    }
  }
}

export const updateFailureAction = <T = any>(entityType: EntityTypes, error: T, opts?) => {
  return {
    type: crudActionType("update", entityType, "failure"),
    payload: {
      opts,
      error
    }
  }
}

export const loadRequest = async (entityType, dispatch, f) => {
  try {
    const data = await f()
    dispatch(loadSuccessAction(entityType, data))
    return data
  } catch (e) {
    log.error(e)
    dispatch(loadFailureAction(entityType, e))
  }
}

export const loadFailureAction = <T = any>(entityType: EntityTypes, error: T, opts?) => {
  return {
    type: crudActionType("load", entityType, "failure"),
    payload: {
      opts,
      error
    }
  }
}

export const loadSuccessAction = <T = any>(entityType: EntityTypes, result: T, opts?) => {
  return {
    type: crudActionType("load", entityType, "success"),
    payload: {
      opts,
      result
    }
  }
}

export const useDispatchUpdateSuccess = <T=any>(entityType: EntityTypes) => {
  const dispatch = useDispatch()
  return (value: T) => {
    dispatch(updateSuccessAction(entityType, value))
  }
}

const createActionFactory = (operationType: CRUD_OPERATIONS) => {
  const createAction = (entityType: EntityTypes, apiName) => {
    const base = `${operationType}-${entityType}`
    const request = `${base}-request`
    const success = `${base}-success`
    const failure = `${base}-failure`
    const funcname = `async${camelize(operationType)}${camelize(entityType)}`
    const apiFunc = apiName || guessAPIName(operationType, entityType)

    const action = opts => {
      const asyncAction = async (dispatch, getState, { api }) => {
        info(
          'crud action==> apiFunc: %s, request: %s, opts: %O',
          apiFunc,
          request,
          opts
        )
        dispatch(actionRequest(request, opts))
        try {
          const data = (await api[apiFunc](opts, getState)) || {}
          dispatch(actionSuccess(success, opts, data))
          return data
        } catch (err) {
          log.error(`API.${apiFunc}`, err)
          dispatch(actionFailure(failure, opts, err))
          throw err
        }
      }
      return asyncAction
    }

    action.BASE = base
    action.REQUEST = request
    action.SUCCESS = success
    action.FAILURE = failure

    return action
  }
  return createAction
}

export default {
  create: createActionFactory('insert'),
  read: createActionFactory('load'),
  update: createActionFactory('update'),
  delete: createActionFactory('delete'),
  upsert: createActionFactory('upsert'),
  any: createActionFactory // the factory function itself
}
